/*  Daily Observation Report – Detail Page
    • Consistent PDF generation (shared pdfStyles)
    • Single‑column responsive layout
*/
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { format, parseISO, isValid } from 'date-fns';
import pdfMake   from 'pdfmake/build/pdfmake';
import pdfFonts  from 'pdfmake/build/vfs_fonts';
import {
  FiClipboard, FiCalendar, FiUser, FiClock,
  FiMessageCircle, FiEdit, FiKey, FiDownload,
} from 'react-icons/fi';
import {
  arrayUnion, doc, onSnapshot, Timestamp, updateDoc,
} from 'firebase/firestore';
import { toast } from 'react-toastify';
import { motion, AnimatePresence } from 'framer-motion';

import { AuthContext } from '../contexts/AuthContext';
import { db }           from '../utils/firebaseConfig';
import { pdfStyles }    from '../utils/pdfStyles';
import { createCommentNotification } from '../utils/notificationUtils';

pdfMake.vfs = pdfFonts.vfs;

/* ------------------------------------------------ PDF Constants ---- */
export const PDF_CONFIG = {
  BANNER_WIDTH: 515,
  PAGE_MARGINS: [40, 20, 40, 40],
  COLORS: {
    primary: '#2563eb',      // Blue-600
    secondary: '#3b82f6',    // Blue-500
    accent: '#93c5fd',       // Blue-300
    success: '#16a34a',      // Green-600
    warning: '#d97706',      // Amber-600
    danger: '#dc2626',       // Red-600
    tableHeader: '#1e40af',  // Blue-800
    tableAlt: '#f8fafc',     // Slate-50
    text: '#1e293b',         // Slate-800
    lightText: '#64748b',    // Slate-500
    border: '#cbd5e1',       // Slate-300
    headerBg: '#dbeafe',     // Blue-100
    dangerBg: '#fee2e2',     // Red-100
    successBg: '#dcfce7',    // Green-100
  }
};

/* Enhanced PDF Styles */
export const enhancedPdfStyles = {
  ...pdfStyles,
  header: {
    fontSize: 24,
    bold: true,
    color: PDF_CONFIG.COLORS.primary,
    margin: [0, 0, 0, 8],
  },
  subheader: {
    fontSize: 11,
    color: PDF_CONFIG.COLORS.text,
    margin: [0, 0, 0, 4],
  },
  sectionHeader: {
    fontSize: 14,
    bold: true,
    color: PDF_CONFIG.COLORS.primary,
    margin: [0, 15, 0, 8],
  },
  content: {
    fontSize: 10,
    color: PDF_CONFIG.COLORS.text,
    lineHeight: 1.5,
    margin: [0, 0, 0, 10],
  },
  footerText: {
    fontSize: 8,
    color: PDF_CONFIG.COLORS.lightText,
  },
  reportNumber: {
    fontSize: 10,
    color: PDF_CONFIG.COLORS.lightText,
    alignment: 'right',
  },
  bannerText: {
    fontSize: 26,
    bold: true,
    color: PDF_CONFIG.COLORS.primary,
  },
  signatureLabel: {
    fontSize: 9,
    bold: true,
    color: PDF_CONFIG.COLORS.text,
  },
  signatureDate: {
    fontSize: 9,
    color: PDF_CONFIG.COLORS.lightText,
  },
  fieldLabel: {
    fontSize: 10,
    bold: true,
    color: PDF_CONFIG.COLORS.primary,
    margin: [0, 2, 0, 0],
  },
  commentHeader: {
    fontSize: 8,
    italics: true,
    color: PDF_CONFIG.COLORS.lightText,
    margin: [0, 5, 0, 1],
  },
  commentText: {
    fontSize: 9,
    color: PDF_CONFIG.COLORS.text,
    margin: [0, 0, 0, 5],
  },
  italicText: {
    fontSize: 9,
    italics: true,
    color: PDF_CONFIG.COLORS.lightText,
  },
  infoBox: {
    fontSize: 9,
    color: PDF_CONFIG.COLORS.text,
    fillColor: PDF_CONFIG.COLORS.headerBg,
    margin: [8, 8],
  },
};

/* ---------- helpers ---------- */
const getBase64ImageFromURL = async (url) => {
  const full = url.startsWith('/') ? `${process.env.PUBLIC_URL || ''}${url}` : url;
  const res  = await fetch(`${full}?t=${Date.now()}`, { cache: 'no-store' });
  if (!res.ok) throw new Error('Image fetch failed');
  const blob  = await res.blob();
  return new Promise((r, j) => { const f=new FileReader(); f.onloadend=()=>r(f.result); f.onerror=j; f.readAsDataURL(blob); });
};
const toDate = d => d instanceof Timestamp ? d.toDate() : typeof d==='string' ? parseISO(d) : d;
const fmtDate     = d => isValid(toDate(d)) ? format(toDate(d),'MMMM d, yyyy')      : 'N/A';
const fmtDateTime = d => isValid(toDate(d)) ? format(toDate(d),'MMMM d, yyyy h:mm a'): 'N/A';
const formatPdfDate = (date, formatStr = 'MMMM d, yyyy') => {
  if (!date) return 'N/A';
  const parsed = toDate(date);
  return isValid(parsed) ? format(parsed, formatStr) : 'N/A';
};

/* ---------- PDF builders ---------- */
const buildObservations = dor => {
  const sections = [];
  
  sections.push({ text:'Observations', style:'sectionHeader' });
  
  // Positive Observations
  sections.push(
    { text:'Positive Observations', style:'fieldLabel' },
    {
      margin: [0, 5, 0, 10],
      table: {
        widths: ['*'],
        body: [
          [
            {
              text: dor.positiveObservations || 'None provided.',
              style: 'content',
              fillColor: 'white',
              border: [true, true, true, true],
              borderColor: [PDF_CONFIG.COLORS.border, PDF_CONFIG.COLORS.border, PDF_CONFIG.COLORS.border, PDF_CONFIG.COLORS.border],
              margin: [10, 10, 10, 10],
            }
          ]
        ]
      },
      layout: {
        hLineWidth: () => 1,
        vLineWidth: () => 1,
        hLineColor: () => PDF_CONFIG.COLORS.border,
        vLineColor: () => PDF_CONFIG.COLORS.border,
        paddingLeft: () => 0,
        paddingRight: () => 0,
        paddingTop: () => 0,
        paddingBottom: () => 0,
        fillColor: () => null,
      }
    }
  );
  
  // Negative Observations
  sections.push(
    { text:'Negative Observations', style:'fieldLabel' },
    {
      margin: [0, 5, 0, 10],
      table: {
        widths: ['*'],
        body: [
          [
            {
              text: dor.negativeObservations || 'None provided.',
              style: 'content',
              fillColor: 'white',
              border: [true, true, true, true],
              borderColor: [PDF_CONFIG.COLORS.border, PDF_CONFIG.COLORS.border, PDF_CONFIG.COLORS.border, PDF_CONFIG.COLORS.border],
              margin: [10, 10, 10, 10],
            }
          ]
        ]
      },
      layout: {
        hLineWidth: () => 1,
        vLineWidth: () => 1,
        hLineColor: () => PDF_CONFIG.COLORS.border,
        vLineColor: () => PDF_CONFIG.COLORS.border,
        paddingLeft: () => 0,
        paddingRight: () => 0,
        paddingTop: () => 0,
        paddingBottom: () => 0,
        fillColor: () => null,
      }
    }
  );
  
  // Recommendations
  sections.push(
    { text:'Recommendations', style:'fieldLabel' },
    {
      margin: [0, 5, 0, 10],
      table: {
        widths: ['*'],
        body: [
          [
            {
              text: dor.recommendations || 'None provided.',
              style: 'content',
              fillColor: 'white',
              border: [true, true, true, true],
              borderColor: [PDF_CONFIG.COLORS.border, PDF_CONFIG.COLORS.border, PDF_CONFIG.COLORS.border, PDF_CONFIG.COLORS.border],
              margin: [10, 10, 10, 10],
            }
          ]
        ]
      },
      layout: {
        hLineWidth: () => 1,
        vLineWidth: () => 1,
        hLineColor: () => PDF_CONFIG.COLORS.border,
        vLineColor: () => PDF_CONFIG.COLORS.border,
        paddingLeft: () => 0,
        paddingRight: () => 0,
        paddingTop: () => 0,
        paddingBottom: () => 0,
        fillColor: () => null,
      }
    }
  );
  
  return sections;
};

const buildComments = dor => {
  const out=[{ text:'Comments', style:'sectionHeader' }];
  
  if(!dor.comments?.length){ 
    out.push({ text:'No comments yet.', style:'italicText', margin: [0, 5, 0, 10] }); 
    return out; 
  }
  
  const commentRows = dor.comments.map(c => [
    {
      fillColor: PDF_CONFIG.COLORS.headerBg,
      border: [true, true, true, true],
      borderColor: [PDF_CONFIG.COLORS.border, PDF_CONFIG.COLORS.border, PDF_CONFIG.COLORS.border, PDF_CONFIG.COLORS.border],
      margin: [5, 5, 5, 5],
      stack: [
        { 
          text: `${fmtDateTime(c.timestamp)} – ${c.userName} (${c.role === 'trainee' ? 'Officer' : 'FTO'})`, 
          style: 'commentHeader' 
        },
        { text: c.text, style: 'commentText' },
      ]
    }
  ]);
  
  out.push({
    margin: [0, 5, 0, 10],
    table: {
      widths: ['*'],
      body: commentRows
    },
    layout: {
      hLineWidth: () => 0,
      vLineWidth: () => 0,
      paddingLeft: () => 0,
      paddingRight: () => 0,
      paddingTop: () => 0,
      paddingBottom: () => 0,
    }
  });
  
  return out;
};

const buildSignatures = dor => {
  const sections = [];
  
  /* Enhanced signature block */
  sections.push({ text: '', margin: [0, 0, 0, 40] });
  sections.push({
    columns: [
      {
        width: '48%',
        stack: [
          {
            canvas: [
              {
                type: 'line',
                x1: 0, y1: 0,
                x2: 200, y2: 0,
                lineWidth: 1,
                lineColor: PDF_CONFIG.COLORS.text,
                dash: { length: 1 },
              }
            ],
            margin: [0, 20, 0, 5],
          },
          { 
            text: "Probationary Officer's Signature", 
            style: 'signatureLabel',
            color: PDF_CONFIG.COLORS.lightText,
          },
          { 
            text: `${dor.traineeName || 'Unknown'} ${dor.traineeIdNumber ? `(#${dor.traineeIdNumber})` : ''}`,
            fontSize: 9,
            color: PDF_CONFIG.COLORS.text,
            margin: [0, 2, 0, 5],
          },
          { 
            text: 'Date: _____________________', 
            style: 'signatureDate',
            color: PDF_CONFIG.COLORS.lightText,
          },
        ],
      },
      { width: '4%', text: '' },
      {
        width: '48%',
        stack: [
          {
            canvas: [
              {
                type: 'line',
                x1: 0, y1: 0,
                x2: 200, y2: 0,
                lineWidth: 1,
                lineColor: PDF_CONFIG.COLORS.text,
                dash: { length: 1 },
              }
            ],
            margin: [0, 20, 0, 5],
            alignment: 'right',
          },
          { 
            text: "Field Training Officer's Signature", 
            style: 'signatureLabel', 
            alignment: 'right',
            color: PDF_CONFIG.COLORS.lightText,
          },
          { 
            text: `${dor.trainerName || 'Unknown'} ${dor.trainerIdNumber ? `(#${dor.trainerIdNumber})` : ''}`, 
            fontSize: 9,
            alignment: 'right',
            color: PDF_CONFIG.COLORS.text,
            margin: [0, 2, 0, 5],
          },
          { 
            text: 'Date: _____________________', 
            style: 'signatureDate', 
            alignment: 'right',
            color: PDF_CONFIG.COLORS.lightText,
          },
        ],
      },
    ],
  });
  
  return sections;
};

export const getDORPdfContentDefinition = async dor => {
  if (!dor) throw new Error('Invalid DOR data for PDF generation');
  
  const content = [];
  
  /* Banner with fallback */
  try {
    const banner = await getBase64ImageFromURL('/banner.png');
    content.push({ 
      image: banner, 
      width: PDF_CONFIG.BANNER_WIDTH, 
      alignment: 'center', 
      margin: [0, 0, 0, 10] 
    });
  } catch (error) {
    console.warn('Failed to load banner image for PDF:', error);
    content.push({
      text: 'FIELD TRAINING PROGRAM',
      style: 'bannerText',
      alignment: 'center',
      margin: [0, 0, 0, 10]
    });
  }
  
  /* Report number at top right */
  content.push({
    text: `Report Number: ${dor.reportId || 'N/A'}`,
    style: 'reportNumber',
    alignment: 'right',
    margin: [0, 0, 0, 5],
  });
  
  /* Title block with enhanced styling */
  content.push(
    { text: 'Daily Observation Report', style: 'header' },
    { 
      canvas: [
        {
          type: 'line',
          x1: 0, y1: 0,
          x2: 515, y2: 0,
          lineWidth: 2,
          lineColor: PDF_CONFIG.COLORS.secondary,
        }
      ],
      margin: [0, 0, 0, 10]
    }
  );

  /* Report Information Section */
  content.push({
    table: {
      widths: ['*'],
      body: [
        [
          {
            stack: [
              {
                table: {
                  widths: ['50%', '50%'],
                  body: [
                    [
                      { 
                        text: `Date: ${formatPdfDate(dor.date)}`, 
                        style: 'infoBox',
                        border: [true, true, false, true],
                        borderColor: [PDF_CONFIG.COLORS.border, PDF_CONFIG.COLORS.border, PDF_CONFIG.COLORS.border, PDF_CONFIG.COLORS.border],
                      },
                      { 
                        text: `Shift Length: ${dor.shiftLength} hours`, 
                        style: 'infoBox',
                        border: [false, true, true, true],
                        borderColor: [PDF_CONFIG.COLORS.border, PDF_CONFIG.COLORS.border, PDF_CONFIG.COLORS.border, PDF_CONFIG.COLORS.border],
                      },
                    ],
                    [
                      { 
                        text: `Probationary Officer: ${dor.traineeName || 'Unknown'} ${dor.traineeIdNumber ? `(#${dor.traineeIdNumber})` : ''}`, 
                        style: 'infoBox',
                        border: [true, false, false, true],
                        borderColor: [PDF_CONFIG.COLORS.border, PDF_CONFIG.COLORS.border, PDF_CONFIG.COLORS.border, PDF_CONFIG.COLORS.border],
                      },
                      { 
                        text: `FTO: ${dor.trainerName || 'Unknown'} ${dor.trainerIdNumber ? `(#${dor.trainerIdNumber})` : ''}`, 
                        style: 'infoBox',
                        border: [false, false, true, true],
                        borderColor: [PDF_CONFIG.COLORS.border, PDF_CONFIG.COLORS.border, PDF_CONFIG.COLORS.border, PDF_CONFIG.COLORS.border],
                      },
                    ]
                  ]
                },
                layout: {
                  hLineWidth: (i, node) => 1,
                  vLineWidth: (i, node) => 1,
                  hLineColor: () => PDF_CONFIG.COLORS.border,
                  vLineColor: () => PDF_CONFIG.COLORS.border,
                  paddingLeft: () => 0,
                  paddingRight: () => 0,
                  paddingTop: () => 0,
                  paddingBottom: () => 0,
                }
              }
            ],
            fillColor: 'white',
            border: [false, false, false, false],
          }
        ]
      ]
    },
    layout: 'noBorders',
    margin: [0, 0, 0, 20],
  });
  
  content.push(...buildObservations(dor), ...buildComments(dor), ...buildSignatures(dor));
  
  /* Footer */
  content.push({
    text: `Generated on ${format(new Date(), 'MMMM d, yyyy \'at\' h:mm a')}`,
    style: 'footerText',
    alignment: 'center',
    margin: [0, 20, 0, 0],
  });
  
  return content;
};

/* ----------------------- Loading Skeleton ----------------------- */
const DORDetailSkeleton = () => (
  <div className="min-h-screen bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark p-3 sm:p-6">
    <div className="max-w-3xl mx-auto space-y-6 animate-pulse">
      {/* PDF Button Skeleton */}
      <div className="hidden lg:flex justify-end mb-4 lg:mb-6">
        <div className="h-11 w-24 bg-secondary-light dark:bg-secondary-dark rounded-md"></div>
      </div>

      {/* Header Card Skeleton */}
      <div className="rounded-lg shadow-lg bg-primary-light dark:bg-primary-dark border border-secondary-light dark:border-secondary-dark p-4 sm:p-6">
        <div className="h-6 w-48 bg-secondary-light dark:bg-secondary-dark rounded mb-3"></div>
        <div className="space-y-2">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="flex items-center gap-2">
              <div className="w-4 h-4 bg-secondary-light dark:bg-secondary-dark rounded"></div>
              <div className="h-4 w-32 bg-secondary-light dark:bg-secondary-dark rounded"></div>
              <div className="h-4 w-24 bg-secondary-light dark:bg-secondary-dark rounded"></div>
            </div>
          ))}
        </div>
      </div>

      {/* Observations Card Skeleton */}
      <div className="rounded-lg shadow-lg bg-primary-light dark:bg-primary-dark border border-secondary-light dark:border-secondary-dark p-4 sm:p-6">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-5 h-5 bg-secondary-light dark:bg-secondary-dark rounded"></div>
          <div className="h-6 w-32 bg-secondary-light dark:bg-secondary-dark rounded"></div>
        </div>
        <div className="space-y-4">
          <div>
            <div className="h-4 w-24 bg-secondary-light dark:bg-secondary-dark rounded mb-2"></div>
            <div className="space-y-2">
              <div className="h-3 w-full bg-secondary-light dark:bg-secondary-dark rounded"></div>
              <div className="h-3 w-3/4 bg-secondary-light dark:bg-secondary-dark rounded"></div>
            </div>
          </div>
          <div>
            <div className="h-4 w-28 bg-secondary-light dark:bg-secondary-dark rounded mb-2"></div>
            <div className="space-y-2">
              <div className="h-3 w-full bg-secondary-light dark:bg-secondary-dark rounded"></div>
              <div className="h-3 w-2/3 bg-secondary-light dark:bg-secondary-dark rounded"></div>
            </div>
          </div>
        </div>
      </div>

      {/* Recommendations Card Skeleton */}
      <div className="rounded-lg shadow-lg bg-primary-light dark:bg-primary-dark border border-secondary-light dark:border-secondary-dark p-4 sm:p-6">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-5 h-5 bg-secondary-light dark:bg-secondary-dark rounded"></div>
          <div className="h-6 w-36 bg-secondary-light dark:bg-secondary-dark rounded"></div>
        </div>
        <div className="space-y-2">
          <div className="h-3 w-full bg-secondary-light dark:bg-secondary-dark rounded"></div>
          <div className="h-3 w-4/5 bg-secondary-light dark:bg-secondary-dark rounded"></div>
        </div>
      </div>

      {/* Comments Card Skeleton */}
      <div className="rounded-lg shadow-lg bg-primary-light dark:bg-primary-dark border border-secondary-light dark:border-secondary-dark p-4 sm:p-6">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-5 h-5 bg-secondary-light dark:bg-secondary-dark rounded"></div>
          <div className="h-6 w-24 bg-secondary-light dark:bg-secondary-dark rounded"></div>
        </div>
        <div className="space-y-4">
          {[1, 2].map((i) => (
            <div key={i} className="p-3 rounded-lg bg-secondary-light/20 dark:bg-secondary-dark/20">
              <div className="h-3 w-48 bg-secondary-light dark:bg-secondary-dark rounded mb-2"></div>
              <div className="space-y-2">
                <div className="h-3 w-full bg-secondary-light dark:bg-secondary-dark rounded"></div>
                <div className="h-3 w-3/4 bg-secondary-light dark:bg-secondary-dark rounded"></div>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-4 space-y-3">
          <div className="h-24 w-full bg-secondary-light dark:bg-secondary-dark rounded-lg"></div>
          <div className="h-10 w-32 bg-secondary-light dark:bg-secondary-dark rounded"></div>
        </div>
      </div>

      {/* Report ID Skeleton */}
      <div className="h-3 w-24 bg-secondary-light dark:bg-secondary-dark rounded ml-auto"></div>
    </div>
  </div>
);

/* ----------------------- React page ----------------------- */
export default function DORDetailPage(){
  const { dorId } = useParams();
  const navigate  = useNavigate();
  const { currentUser, role, userName } = useContext(AuthContext);

  const [dor,setDor]               = useState(null);
  const [newComment,setNewComment] = useState('');
  const [loading,setLoading]       = useState(true);
  const [submitting,setSubmitting] = useState(false);

  useEffect(()=>{
      if(!currentUser){ navigate('/login'); return; }
    
    const reportDocRef = doc(db,'dors',dorId);
    
    // Set up the real-time listener
    const unsubscribe = onSnapshot(reportDocRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        const dt = toDate(data.date); // Ensure toDate is defined if used here
        const rid = data.reportId || `DOR-${format(dt,'yyyyMMdd')}-${docSnap.id.slice(0,6)}`;
        setDor({...data, id: docSnap.id, date:dt, reportId:rid});
        setLoading(false);
      } else {
        toast.error('DOR not found');
        navigate('/');
        setLoading(false);
      }
    }, (error) => {
      console.error("Error fetching DOR in real-time:", error);
      toast.error('Error fetching DOR data.');
      setLoading(false);
    });

    // Cleanup listener on component unmount
    return () => unsubscribe();
    
  },[dorId, currentUser, navigate]); // Ensure all dependencies are listed

  const addComment=async()=>{
    if(!newComment.trim()){ toast.error('Comment cannot be empty'); return; }
    setSubmitting(true);
    try{
      const reportDocRef=doc(db,'dors',dorId);
      const commentToAdd={ text:newComment.trim(), userId:currentUser.uid, userName, timestamp:Timestamp.now(), role };
      
      // Update the document. onSnapshot will handle the UI update.
      await updateDoc(reportDocRef,{ comments:arrayUnion(commentToAdd) });
      
      // Send notification about the comment
      try {
        await createCommentNotification(
          dorId,         // reportId (Firestore doc ID)
          'DOR',         // reportType
          currentUser.uid, // commentingUserId
          userName,      // commentingUserName
          role,          // commentingUserRole
          newComment.trim() // commentText
        );
        console.log(`Attempted to send notification from ${role} user.`);
      } catch (notifError) {
        console.error('Failed to send comment notification (from DORDetailPage):', notifError);
      }
      
      setNewComment(''); 
      // No need to toast.success here, or it might appear twice due to re-render. 
      // The visual update of the comment list serves as confirmation.
      // toast.success('Comment added'); // Optional: if you prefer explicit toast
    }catch(e){ 
      console.error("Error adding comment:", e); 
      toast.error('Add comment failed'); 
    }
    finally{ setSubmitting(false); }
  };

  const downloadPdf=async()=>{
    if(!dor) return;
    
    const loadingToast = toast.loading('Generating PDF...');
    
    try{
      const content=await getDORPdfContentDefinition(dor);
      const docDefinition = {
        pageMargins: PDF_CONFIG.PAGE_MARGINS,
        content,
        styles: enhancedPdfStyles,
        info: {
          title: `Daily Observation Report - ${dor.traineeName}`,
          author: dor.trainerName,
          subject: `DOR dated ${formatPdfDate(dor.date, 'yyyy-MM-dd')}`,
          keywords: 'field training, daily observation report, DOR, FTO, trainee',
          creator: 'Field Training Management System',
          producer: 'Field Training Management System',
          creationDate: new Date(),
        },
        // Page settings
        pageSize: 'LETTER',
        pageOrientation: 'portrait',
        // Header for pages 2+
        header: (currentPage, pageCount) => {
          if (currentPage === 1) return null;
          return {
            columns: [
              {
                text: `Daily Observation Report - ${dor.traineeName}`,
                style: 'footerText',
                alignment: 'left',
              },
              {
                text: `Page ${currentPage} of ${pageCount}`,
                style: 'footerText',
                alignment: 'right',
              }
            ],
            margin: [40, 10, 40, 0],
          };
        },
      };
      
      pdfMake.createPdf(docDefinition)
             .download(`DOR_${dor.reportId}_${format(dor.date, 'yyyy-MM-dd')}.pdf`);
      
      toast.update(loadingToast, { 
        render: 'PDF generated successfully!', 
        type: 'success', 
        isLoading: false,
        autoClose: 3000 
      });
    }catch(e){ 
      console.error('PDF generation error:', e); 
      toast.update(loadingToast, { 
        render: 'Failed to generate PDF', 
        type: 'error', 
        isLoading: false,
        autoClose: 5000 
      });
    }
  };

  /* tailwind shortcuts */
  const card   ='rounded-lg shadow-lg bg-primary-light dark:bg-primary-dark border border-secondary-light dark:border-secondary-dark';
  const head   ='font-semibold flex items-center gap-2 text-base sm:text-lg';
  const label  ='font-semibold text-[11px] sm:text-xs';
  const button ='min-h-[44px] px-4 py-2 rounded-md focus-visible:ring-2 focus-visible:ring-blue-500';

  return(
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className="min-h-screen bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark p-3 sm:p-6"
    >
      <motion.div 
        initial={{ opacity: 0, y: 30, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.5, delay: 0.2, ease: "easeOut" }}
        className="max-w-3xl mx-auto"
      >
        <AnimatePresence mode="wait">
          {loading ? (
            <motion.div
              key="loading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <DORDetailSkeleton />
            </motion.div>
          ) : !dor ? (
            <motion.div
              key="not-found"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.3 }}
              className="flex items-center justify-center min-h-screen"
            >
              <div className={`${card} p-8 text-center`}>
                <FiClipboard className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                <h2 className="text-xl font-semibold mb-2">DOR Not Found</h2>
                <p className="text-gray-600 mb-4">The Daily Observation Report you're looking for doesn't exist.</p>
                <button 
                  onClick={() => navigate('/')}
                  className={`${button} bg-blue-600 hover:bg-blue-700 text-white`}
                >
                  Go Back Home
                </button>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="content"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4, ease: "easeOut" }}
            >
              {/* desktop‑only button */}
              {(role==='admin'||role==='trainer') && (
                <motion.div 
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4, delay: 0.3 }}
                  className="hidden lg:flex justify-end mb-4 lg:mb-6"
                >
                  <button onClick={downloadPdf} className={`${button} bg-blue-600 hover:bg-blue-700 text-white shadow flex items-center gap-2`}>
                    <FiDownload className="shrink-0"/> PDF
                  </button>
                </motion.div>
              )}

              {/* header */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.4 }}
                className={`${card} p-4 sm:p-6`}
              >
                <h1 className="text-lg sm:text-xl font-bold mb-3 flex items-center gap-2"><FiClipboard/> Daily Observation Report</h1>
                <div className="space-y-1 text-sm">
                  <p className="flex items-center gap-2"><FiCalendar/><span className={label}>Date:</span>{fmtDate(dor.date)}</p>
                  <p className="flex items-center gap-2"><FiClock   /><span className={label}>Shift:</span>{dor.shiftLength} h</p>
                  <p className="flex items-center gap-2"><FiUser    /><span className={label}>FTO:</span>{dor.trainerName || 'Unknown'} {dor.trainerIdNumber}</p>
                  <p className="flex items-center gap-2"><FiUser    /><span className={label}>Officer:</span>{dor.traineeName || 'Unknown'} {dor.traineeIdNumber}</p>
                </div>
              </motion.div>

              {/* observations */}
              <motion.section 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.5 }}
                className={`${card} p-4 sm:p-6 mt-6`}
              >
                <h2 className={head}><FiEdit/> Observations</h2>
                <p className="mt-3 mb-2 text-xs sm:text-sm"><span className={label}>Positive:</span><br/>{dor.positiveObservations || 'None.'}</p>
                <p className="text-xs sm:text-sm"><span className={label}>Negative:</span><br/>{dor.negativeObservations || 'None.'}</p>
              </motion.section>

              {/* recommendations */}
              <motion.section 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.6 }}
                className={`${card} p-4 sm:p-6 mt-6`}
              >
                <h2 className={head}><FiKey/> Recommendations</h2>
                <p className="mt-3 text-xs sm:text-sm">{dor.recommendations || 'None.'}</p>
              </motion.section>

              {/* comments */}
              <motion.section 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.7 }}
                className={`${card} p-4 sm:p-6 mt-6`}
              >
                <h2 className={head}><FiMessageCircle/> Comments</h2>

                {dor.comments?.length ? (
                  <ul className="mt-3 space-y-4">
                    {dor.comments.map((c,i)=>(
                      <motion.li 
                        key={i}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.3, delay: 0.8 + (i * 0.1) }}
                        className="p-3 rounded-lg bg-secondary-light/20 dark:bg-secondary-dark/20 hover:bg-secondary-light/30 dark:hover:bg-secondary-dark/30 transition"
                      >
                        <p className="text-[11px] sm:text-xs mb-1 text-text-light/70 dark:text-text-dark/70">
                          {fmtDateTime(c.timestamp)} • {c.role==='trainee' ? 'Officer' : 'FTO'} – {c.userName}
                        </p>
                        <p className="text-xs sm:text-sm">{c.text}</p>
                      </motion.li>
                    ))}
                  </ul>
                ) : (
                  <p className="italic mt-3 text-xs sm:text-sm text-text-light/60 dark:text-text-dark/60">No comments yet.</p>
                )}

                {['trainee','trainer','admin'].includes(role) && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: 0.9 }}
                  >
                    <textarea
                      rows={4}
                      value={newComment}
                      onChange={e=>setNewComment(e.target.value)}
                      placeholder="Add a comment…"
                      className="mt-4 w-full p-3 rounded-lg bg-secondary-light dark:bg-secondary-dark border border-secondary-light dark:border-secondary-dark text-xs sm:text-sm focus-visible:ring-2 focus-visible:ring-blue-500"/>
                    <button
                      onClick={addComment}
                      disabled={submitting}
                      className={`${button} mt-3 bg-blue-600 hover:bg-blue-700 text-white shadow disabled:opacity-50`}>
                      {submitting ? 'Adding…' : 'Add Comment'}
                    </button>
                  </motion.div>
                )}
              </motion.section>

              <motion.p 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.4, delay: 1.0 }}
                className="mt-6 text-[11px] text-right pr-20 text-text-light/60 dark:text-text-dark/60"
              >
                Report #: {dor.reportId}
              </motion.p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* mobile / tablet floating button */}
        {(role==='admin'||role==='trainer') && !loading && dor && (
          <motion.button 
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4, delay: 0.8 }}
            onClick={downloadPdf}
            className="lg:hidden fixed bottom-28 right-4 z-[1300] bg-blue-600 hover:bg-blue-700 text-white rounded-full shadow-xl p-4 focus-visible:ring-2 focus-visible:ring-blue-500"
          >
            <FiDownload className="w-5 h-5 shrink-0"/>
          </motion.button>
        )}
      </motion.div>
    </motion.div>
  );
}
